import { useEffect, useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOPIFY_STORE_URL = process.env.REACT_APP_SHOPIFY_STORE_URL;
const SHOPIFY_CURRENCY = process.env.REACT_APP_SHOPIFY_CURRENCY;
const SHOPIFY_COLLECTIONS = [
  { handle: 'bottoms', title: 'BOTTOMS' },
  { handle: 'hoodies', title: 'Hoodies' },
  { handle: 'jackets', title: 'Jackets & Coats' },
  { handle: 'jersey', title: 'Sportswear' },
  { handle: 'sweatshirts-long-sleeve-shirts', title: 'Sweatshirts & long sleeve shirts' },
  { handle: 'tshirt', title: 'T-Shirts' },
];
let catalogCache = null;
let catalogPromise = null;

const inferZone = (node) =>
  /bottom|pant|cargo|trouser|denim|jean|skirt|short/i.test(`${node.productType} ${node.title}`)
    ? 'bottom'
    : 'top';

export function mapShopifyProduct(node) {
  const options = node.options || [];
  const variants = (node.variants?.edges || []).map(({ node: variant }) => mapVariant(variant, options));
  const colorOpt = options.find((o) => /colou?r/i.test(o.name));
  const collections = (node.collections?.edges || []).map((edge) => edge.node);
  const media = mapMedia(node.images?.edges, node.title);
  const firstAvailable = variants.find((variant) => variant.available) || variants[0];
  const selectedColor = firstAvailable?.selectedOptions?.find((option) => /colou?r/i.test(option.name))?.value;
  return {
    id: node.handle,
    name: node.title,
    price: variants[0]?.price ?? 0,
    currency: variants[0]?.currency ?? 'USD',
    category: collections[0]?.title || node.productType || 'Essentials',
    collections,
    zone: inferZone(node),
    color: selectedColor || colorOpt?.values?.[0] || '—',
    tag: node.tags?.[0] || 'New',
    image: firstAvailable?.imageUrl || node.featuredImage?.url || media[0]?.url,
    images: media.map((image) => image.url),
    media,
    description: node.description,
    options,
    variants,
  };
}

const mapVariant = (variant, options) => ({
  id: variant.id,
  title: variant.title,
  available: variant.availableForSale,
  price: parseFloat(variant.price.amount),
  currency: variant.price.currencyCode,
  selectedOptions: variant.selectedOptions?.length ? variant.selectedOptions : fallbackOptions(variant.title, options),
  inventoryQuantity: Number.isInteger(variant.inventoryQuantity) ? variant.inventoryQuantity : null,
  imageUrl: variant.image?.url || null,
});

const fallbackOptions = (title, options) => title.split(' / ').map((value, index) => ({
  name: options[index]?.name,
  value,
})).filter((option) => option.name && option.value);

const mapMedia = (edges = [], title) => edges.map(({ node: image }) => ({
  id: image.id || image.url,
  url: image.url,
  altText: image.altText || title,
  variantIds: image.variantIds || [],
}));

const unavailableCatalog = () => ({
  products: [],
  collections: [],
  source: 'unavailable',
  loading: false,
  error: 'Live Shopify catalog is unavailable',
});

const pause = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));

const fetchJson = async (url, attemptsRemaining = 2) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Shopify catalog request failed: ${response.status}`);
    return response.json();
  } catch (error) {
    if (!attemptsRemaining) throw error;
    await pause(350);
    return fetchJson(url, attemptsRemaining - 1);
  }
};

const mapNativeVariant = (variant, options, images) => ({
  id: String(variant.id),
  title: variant.title,
  available: variant.available,
  price: Number(variant.price),
  currency: SHOPIFY_CURRENCY,
  selectedOptions: options.map((option, index) => ({ name: option.name, value: variant[`option${index + 1}`] })).filter((option) => option.value),
  inventoryQuantity: Number.isInteger(variant.inventory_quantity) ? variant.inventory_quantity : null,
  imageUrl: variant.featured_image?.src || images.find((image) => image.variant_ids?.includes(variant.id))?.src || null,
});

const mapNativeProduct = (product, memberships) => {
  const variants = product.variants.map((variant) => mapNativeVariant(variant, product.options, product.images));
  const firstAvailable = variants.find((variant) => variant.available) || variants[0];
  const collections = memberships.get(product.id) || [];
  const tags = Array.isArray(product.tags) ? product.tags : product.tags?.split(',');
  const media = product.images.map((image) => ({
    id: String(image.id),
    url: image.src,
    altText: image.alt || product.title,
    variantIds: image.variant_ids?.map(String) || [],
  }));
  return {
    id: product.handle,
    name: product.title,
    price: firstAvailable?.price ?? 0,
    currency: SHOPIFY_CURRENCY,
    category: collections[0]?.title || product.product_type || 'Shopify',
    collections,
    zone: inferZone({ productType: product.product_type, title: product.title }),
    color: firstAvailable?.selectedOptions.find((option) => /colou?r/i.test(option.name))?.value || product.options[0]?.values?.[0] || '—',
    tag: tags?.map((tag) => tag.trim()).find(Boolean) || 'Shopify',
    image: firstAvailable?.imageUrl || media[0]?.url,
    images: media.map((image) => image.url),
    media,
    description: product.body_html?.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || '',
    options: product.options,
    variants,
  };
};

const fetchPublicCatalog = async () => {
  const catalog = await fetchJson(`${SHOPIFY_STORE_URL}/products.json?limit=250`);
  const collectionFeeds = [];
  for (const collection of SHOPIFY_COLLECTIONS) {
    const collectionCatalog = await fetchJson(`${SHOPIFY_STORE_URL}/collections/${collection.handle}/products.json?limit=250`);
    collectionFeeds.push({ collection, products: collectionCatalog.products });
  }
  const collections = collectionFeeds.map(({ collection, products }) => ({ ...collection, productCount: products.length }));
  const memberships = new Map();
  collectionFeeds.forEach(({ collection, products }) => products.forEach((product) => {
    memberships.set(product.id, [...(memberships.get(product.id) || []), collection]);
  }));
  return { products: catalog.products.map((product) => mapNativeProduct(product, memberships)), collections };
};

const fetchProxyCatalog = () => fetchJson(`${API}/shopify/products`);

const fetchCatalog = () => {
  if (!catalogPromise) catalogPromise = fetchPublicCatalog().catch(fetchProxyCatalog);
  return catalogPromise;
};

const normalizeCatalog = (data) => {
  if (!data?.products?.length) return unavailableCatalog();
  catalogCache = {
    products: data.products[0].name ? data.products : data.products.map(mapShopifyProduct),
    collections: data.collections || [],
    source: 'shopify',
    loading: false,
    error: null,
  };
  return catalogCache;
};

export function useProducts() {
  const [state, setState] = useState(
    catalogCache || { products: [], collections: [], source: 'syncing', loading: true, error: null }
  );
  useEffect(() => {
    let active = true;
    fetchCatalog()
      .then((data) => {
        if (active) setState(normalizeCatalog(data));
      })
      .catch(() => {
        catalogPromise = null;
        if (active) setState(unavailableCatalog());
      });
    return () => {
      active = false;
    };
  }, []);
  return state;
}
